/**
 * The benchmark's peers and the values entered for them — build 6B.
 *
 * The peer list was fixed in code and an entered value could not be taken
 * back. Here the airport adds or removes a peer (removing one removes its
 * values, so it cannot rank anything), and changes or deletes any value,
 * peer or own. Everything stays in the benchmark's note: each save is a new
 * note with who and when, so the one before it is kept.
 */
import { Fragment, useState } from "react";
import { Button, Card, Field, Input, Select, Table } from "../../design/components";
import { METRICS, type BenchmarkEntered, type Peer, type PeerSet } from "../../domain/benchmark";
import {
  addPeer,
  PEER_SETS,
  peerRefusal,
  peerValueRefusal,
  removePeer,
  removePeerValue,
} from "../../domain/executiveEntries";
import { t } from "../../i18n";
import s from "./EntryForms.module.css";

interface ValueRow {
  who: string;
  name: string;
  metric: string;
  value: number;
  year?: number;
  source?: string;
}

export function PeersCard({
  entered,
  list,
  siteCode,
  ready,
  onSave,
}: {
  entered: BenchmarkEntered;
  list: Peer[];
  siteCode: string;
  ready: boolean;
  onSave: (next: BenchmarkEntered) => Promise<void> | void;
}) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [set, setSet] = useState<PeerSet>("regional");
  const [why, setWhy] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const label = (m: string) => t(METRICS.find((x) => x.key === m)?.label ?? m);
  const values: ValueRow[] = [
    ...Object.entries(entered.own ?? {}).map(([metric, v]) => ({
      who: "own",
      name: t("this airport ({code})", { code: siteCode }),
      metric,
      ...v,
    })),
    ...Object.entries(entered.peers ?? {}).flatMap(([who, m]) =>
      Object.entries(m).map(([metric, v]) => ({
        who,
        name: list.find((p) => p.code === who)?.name ?? who,
        metric,
        ...v,
      })),
    ),
  ];
  const count = (c: string) => Object.keys(entered.peers?.[c] ?? {}).length;

  const add = async () => {
    const refused = peerRefusal({ code, name, set }, list, siteCode);
    if (refused) return setWhy(refused);
    setWhy(null);
    await onSave(addPeer(entered, list, { code, name, set }));
    setCode("");
    setName("");
  };

  return (
    <Card
      title="Peers and entered values"
      sub="Add, change or remove any of them."
      collapsible
      collapseKey="benchmark-peers"
      testId="benchmark-peers"
    >
      <div className={s.scroll}>
        <Table>
          <thead>
            <tr>
              <th>{t("Code")}</th>
              <th>{t("Airport")}</th>
              <th>{t("Set")}</th>
              <th className={s.num}>{t("Values")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.code} data-testid={`peer-${p.code}`}>
                <td>{p.code}</td>
                <td>{p.name}</td>
                <td>{t(p.set)}</td>
                <td className={s.num}>{count(p.code)}</td>
                <td>
                  {ready && (
                    <Button size="sm" variant="ghost" onClick={() => void onSave(removePeer(entered, list, p.code))}>
                      {t("Remove")}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      {ready && (
        <div className={s.form} data-testid="peer-add">
          <Field label="ICAO code">
            <Input value={code} maxLength={4} onChange={(e) => setCode(e.target.value.toUpperCase())} />
          </Field>
          <Field label="Airport name">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Peer set">
            <Select value={set} onChange={(e) => setSet(e.target.value as PeerSet)}>
              {PEER_SETS.map((x) => (
                <option key={x} value={x}>
                  {t(x)}
                </option>
              ))}
            </Select>
          </Field>
          <div className={s.actions}>
            <Button onClick={() => void add()}>{t("Add the peer")}</Button>
          </div>
          {why && (
            <p className={`${s.err} ${s.wide}`} role="alert">
              {t(why)}
            </p>
          )}
        </div>
      )}
      <h3 className={s.muted}>{t("Entered values")}</h3>
      {values.length === 0 ? (
        <p className={s.muted}>{t("No value entered yet. Add one from the published figure, with its source.")}</p>
      ) : (
        <div className={s.scroll}>
          <Table>
            <thead>
              <tr>
                <th>{t("Airport")}</th>
                <th>{t("Metric")}</th>
                <th className={s.num}>{t("Value")}</th>
                <th>{t("Year")}</th>
                <th>{t("Source")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {values.map((v) => {
                const id = `${v.who}|${v.metric}`;
                return (
                  <Fragment key={id}>
                    <tr data-testid={`peer-value-${v.who}-${v.metric}`}>
                      <td>{v.name}</td>
                      <td>{label(v.metric)}</td>
                      <td className={s.num}>{v.value}</td>
                      <td>{v.year ?? ""}</td>
                      <td className={s.muted}>{v.source ?? ""}</td>
                      <td>
                        {ready && (
                          <div className={s.actions}>
                            <Button size="sm" onClick={() => setEditing(editing === id ? null : id)}>
                              {t("Change")}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => void onSave(removePeerValue(entered, v.who, v.metric))}
                            >
                              {t("Delete")}
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {editing === id && (
                      <tr className={s.formRow}>
                        <td colSpan={6}>
                          <ValueForm
                            row={v}
                            onCancel={() => setEditing(null)}
                            onSave={async (next) => {
                              const pv = { value: next.value, year: next.year, source: next.source };
                              setEditing(null);
                              await onSave(
                                v.who === "own"
                                  ? { ...entered, own: { ...entered.own, [v.metric]: pv } }
                                  : {
                                      ...entered,
                                      peers: {
                                        ...entered.peers,
                                        [v.who]: { ...entered.peers?.[v.who], [v.metric]: pv },
                                      },
                                    },
                              );
                            }}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}
    </Card>
  );
}

function ValueForm({
  row,
  onSave,
  onCancel,
}: {
  row: ValueRow;
  onSave: (v: { value: number; year?: number; source: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(String(row.value));
  const [year, setYear] = useState(row.year != null ? String(row.year) : "");
  const [source, setSource] = useState(row.source ?? "");
  const [why, setWhy] = useState<string | null>(null);
  const go = async () => {
    const v = {
      value: value.trim() === "" ? Number.NaN : Number(value),
      year: year ? Number(year) : undefined,
      source,
    };
    const refused = peerValueRefusal(v);
    if (refused) return setWhy(refused);
    setWhy(null);
    await onSave({ ...v, source: source.trim() });
  };
  return (
    <div className={s.form} data-testid="peer-value-form">
      <Field label="Value">
        <Input type="number" step="any" value={value} onChange={(e) => setValue(e.target.value)} />
      </Field>
      <Field label="Year">
        <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} />
      </Field>
      <div className={s.wide}>
        <Field label="Source">
          <Input value={source} onChange={(e) => setSource(e.target.value)} />
        </Field>
      </div>
      {why && (
        <p className={`${s.err} ${s.wide}`} role="alert">
          {t(why)}
        </p>
      )}
      <div className={`${s.actions} ${s.wide}`}>
        <Button variant="primary" onClick={() => void go()}>
          {t("Save")}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          {t("Cancel")}
        </Button>
      </div>
    </div>
  );
}
